import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
  Navigate,
} from 'react-router-dom'
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Paper,
  LinearProgress,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useMediaQuery,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { endOfMonth, format, parse, startOfMonth } from 'date-fns'
import {
  CalendarSessionChip,
  DonorPortalScreenMessage,
} from '@/components/donation'
import { CommonButton } from '@/components/common'
import { ChevronLeftIcon, ChevronRightIcon } from '@/icons'
import {
  useGetDonorCenterCalendar,
  useGetDonorCenters,
  useGetProcedureTypes,
} from '@/hooks'
import { useDonorContext } from '@/context'
import { ROUTES, DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import {
  buildResultItem,
  formatDaySessionsTimeSpan,
  getDonorEligibilityForCalendarDay,
  getDonorEligibilityForScheduleRow,
  getScheduledDonationsParams,
  getTenantTodayIso,
  parseSearchByDonorCentersParams,
  pickFirstBookableSessionRow,
  sumRowsAvailableSlots,
  toUTCDateString,
} from '@/utils'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_COMPACT_EVENTS = 4

/** First instant of the tenant's current calendar month (local Date from yyyy-mm-dd). */
const getTenantMonthFloorDate = () => {
  const iso = getTenantTodayIso()
  const [y, m] = iso.split('-').map(Number)
  return new Date(y, m - 1, 1)
}

export const DonorCenterCalendar = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const parsed = useMemo(
    () => parseSearchByDonorCentersParams(searchParams),
    [searchParams]
  )

  const { donorCenters, getDonorCenters } = useGetDonorCenters()
  const { procedureTypes, loadingProcedureTypes, getProcedureTypes } =
    useGetProcedureTypes()
  const {
    donorCenterSessions,
    getDonorCenterCalendar,
    loadingDonorCenterCalendar,
  } = useGetDonorCenterCalendar()
  const { donor, getDonor } = useDonorContext()

  const [viewMonth, setViewMonth] = useState(() => {
    let d
    if (parsed.date) {
      const [y, m] = parsed.date.split('-').map(Number)
      d = new Date(y, m - 1, 1)
    } else {
      d = startOfMonth(new Date())
    }
    const floor = getTenantMonthFloorDate()
    return d.getTime() < floor.getTime() ? floor : d
  })

  const [procedureFilter, setProcedureFilter] = useState(
    () => parsed.procedure_type_id || ''
  )

  const [expandedDays, setExpandedDays] = useState({})
  const [mobileSelectedDateIso, setMobileSelectedDateIso] = useState(null)
  const dayRefs = useRef({})

  useEffect(() => {
    getProcedureTypes()
    getDonorCenters()
  }, [])

  useEffect(() => {
    getDonor()
  }, [getDonor])

  useEffect(() => {
    if (!parsed.donor_center_id) return
    const start = format(startOfMonth(viewMonth), 'yyyy-MM-dd')
    const end = format(endOfMonth(viewMonth), 'yyyy-MM-dd')
    const params = {
      donor_center_id: parsed.donor_center_id,
      start_date: start,
      end_date: end,
    }
    if (procedureFilter) params.procedure_type_id = procedureFilter
    getDonorCenterCalendar(params)
  }, [
    parsed.donor_center_id,
    viewMonth,
    procedureFilter,
    getDonorCenterCalendar,
  ])

  useEffect(() => {
    if (!parsed.date) return
    const el = dayRefs.current[parsed.date]
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [parsed.date, viewMonth, loadingDonorCenterCalendar])

  const donorCenterName = useMemo(() => {
    const id = String(parsed.donor_center_id)
    const row = donorCenters?.find(d => String(d.id) === id)
    return row?.name || 'Donor center'
  }, [donorCenters, parsed.donor_center_id])

  /** Address/phone from first session row (donor-centers API is id+name only). */
  const liveLocationPreview = useMemo(() => {
    const row = donorCenterSessions?.[0]
    if (!row) return null
    const built = buildResultItem(row, {})
    return {
      address: built.address,
      phone: row.phone_number,
    }
  }, [donorCenterSessions])

  const donorCenterLocationFallbackRef = useRef(null)
  const prevDonorCenterIdRef = useRef(parsed.donor_center_id)

  if (prevDonorCenterIdRef.current !== parsed.donor_center_id) {
    prevDonorCenterIdRef.current = parsed.donor_center_id
    donorCenterLocationFallbackRef.current = null
  }

  useEffect(() => {
    if (liveLocationPreview?.address || liveLocationPreview?.phone) {
      donorCenterLocationFallbackRef.current = liveLocationPreview
    }
  }, [liveLocationPreview])

  const locationPreview =
    liveLocationPreview ??
    (loadingDonorCenterCalendar ? donorCenterLocationFallbackRef.current : null)

  const sessionsByDate = useMemo(() => {
    const map = {}
    ;(donorCenterSessions || []).forEach(row => {
      const d = row.date
      if (!d) return
      if (!map[d]) map[d] = []
      map[d].push(row)
    })
    return map
  }, [donorCenterSessions])

  const tenantTodayIso = useMemo(() => getTenantTodayIso(), [])

  /** Future/today days in this month that have at least one session row */
  const availabilityInsight = useMemo(() => {
    let daysWithOpenings = 0
    Object.entries(sessionsByDate).forEach(([dateStr, rows]) => {
      if (dateStr >= tenantTodayIso && rows?.length > 0) {
        daysWithOpenings += 1
      }
    })
    const rows = donorCenterSessions || []
    const sessionRows = rows.length
    let totalSlots = 0
    rows.forEach(row => {
      const raw = row?.available_slots_count
      const n = raw == null || raw === '' ? 0 : Number(raw)
      if (!Number.isNaN(n)) totalSlots += n
    })
    return { daysWithOpenings, sessionRows, totalSlots }
  }, [sessionsByDate, tenantTodayIso, donorCenterSessions])

  /** Per-day desktop-parity state for mobile grid (closed vs no bookable session for donor). */
  const calendarDayMetaByDate = useMemo(() => {
    const y = viewMonth.getFullYear()
    const m = viewMonth.getMonth()
    const dim = new Date(y, m + 1, 0).getDate()
    const map = {}

    for (let day = 1; day <= dim; day += 1) {
      const dateStr = toUTCDateString(new Date(y, m, day))
      const events = sessionsByDate[dateStr] || []
      const isPastDay = dateStr < tenantTodayIso

      const dayHasSelectableSession = events.some(row => {
        const slots = Number(row?.available_slots_count)
        if (Number.isNaN(slots) || slots <= 0) return false
        return getDonorEligibilityForScheduleRow({
          donorId: donor?.id,
          procedureEligibilities: donor?.procedure_eligibilities,
          selectedProcedureTypeId: procedureFilter,
          appointmentDayIso: dateStr,
          procedureTypesOnRow: row?.procedure_types,
        }).selectable
      })

      const hasOpenSlots = events.some(row => {
        const slots = Number(row?.available_slots_count)
        return !Number.isNaN(slots) && slots > 0
      })

      const isClosed =
        !loadingDonorCenterCalendar && !isPastDay && events.length === 0

      // Sessions exist but every slot is booked → "not available" (parity with
      // desktop disabling the session chip), NOT an eligibility problem.
      const isFull =
        !loadingDonorCenterCalendar &&
        !isPastDay &&
        events.length > 0 &&
        !hasOpenSlots

      // Open slots exist but none are bookable for this donor → not eligible.
      const isNotEligible =
        !loadingDonorCenterCalendar &&
        !isPastDay &&
        events.length > 0 &&
        hasOpenSlots &&
        !!donor?.id &&
        !dayHasSelectableSession

      const selectable =
        !isPastDay &&
        !isClosed &&
        !isFull &&
        !isNotEligible &&
        !loadingDonorCenterCalendar

      map[dateStr] = {
        isPastDay,
        isClosed,
        isFull,
        isNotEligible,
        selectable,
      }
    }

    return map
  }, [
    viewMonth,
    sessionsByDate,
    tenantTodayIso,
    loadingDonorCenterCalendar,
    donor?.id,
    donor?.procedure_eligibilities,
    procedureFilter,
  ])

  useEffect(() => {
    if (!isMobile) return
    const monthStart = format(startOfMonth(viewMonth), 'yyyy-MM-dd')
    const monthEnd = format(endOfMonth(viewMonth), 'yyyy-MM-dd')
    const today = getTenantTodayIso()

    const pickDefault = () => {
      const y = viewMonth.getFullYear()
      const mo = viewMonth.getMonth()
      const dim = new Date(y, mo + 1, 0).getDate()
      const selectableDates = []
      for (let day = 1; day <= dim; day += 1) {
        const dateStr = toUTCDateString(new Date(y, mo, day))
        if (dateStr < today) continue
        if (calendarDayMetaByDate[dateStr]?.selectable)
          selectableDates.push(dateStr)
      }
      if (selectableDates.length === 0) return null

      if (
        parsed.date &&
        parsed.date >= monthStart &&
        parsed.date <= monthEnd &&
        selectableDates.includes(parsed.date)
      ) {
        return parsed.date
      }
      if (
        today >= monthStart &&
        today <= monthEnd &&
        selectableDates.includes(today)
      ) {
        return today
      }
      return selectableDates[0]
    }

    setMobileSelectedDateIso(prev => {
      if (
        prev &&
        prev >= monthStart &&
        prev <= monthEnd &&
        calendarDayMetaByDate[prev]?.selectable
      ) {
        return prev
      }
      return pickDefault()
    })
  }, [isMobile, viewMonth, parsed.date, calendarDayMetaByDate])

  const mobileDayEvents = useMemo(
    () =>
      mobileSelectedDateIso != null
        ? sessionsByDate[mobileSelectedDateIso] || []
        : [],
    [mobileSelectedDateIso, sessionsByDate]
  )

  const mobileDaySlots = useMemo(
    () => sumRowsAvailableSlots(mobileDayEvents),
    [mobileDayEvents]
  )

  const mobileTimeSpanLabel = useMemo(
    () => formatDaySessionsTimeSpan(mobileDayEvents),
    [mobileDayEvents]
  )

  const mobileBookableRow = useMemo(() => {
    if (
      !isMobile ||
      mobileSelectedDateIso == null ||
      loadingDonorCenterCalendar
    ) {
      return null
    }
    return pickFirstBookableSessionRow(mobileDayEvents, mobileSelectedDateIso, {
      donor,
      procedureFilter,
    })
  }, [
    isMobile,
    mobileSelectedDateIso,
    loadingDonorCenterCalendar,
    mobileDayEvents,
    donor,
    procedureFilter,
  ])

  const mobileEligibilityChipConfig = useMemo(() => {
    if (
      !isMobile ||
      !donor?.id ||
      mobileSelectedDateIso == null ||
      mobileDayEvents.length === 0 ||
      loadingDonorCenterCalendar
    ) {
      return null
    }
    if (mobileSelectedDateIso < tenantTodayIso) return null

    const dayElig = getDonorEligibilityForCalendarDay({
      donorId: donor.id,
      procedureEligibilities: donor.procedure_eligibilities,
      selectedProcedureTypeId: procedureFilter,
      appointmentDayIso: mobileSelectedDateIso,
      sessionRows: mobileDayEvents,
    })
    if (!dayElig.applies || !dayElig.chip) return null

    if (
      procedureFilter &&
      dayElig.chip.status === 'eligible' &&
      !mobileBookableRow
    ) {
      return { label: 'Full', eligible: false }
    }

    return {
      label: dayElig.chip.label,
      eligible: dayElig.chip.status === 'eligible',
    }
  }, [
    isMobile,
    donor?.id,
    donor?.procedure_eligibilities,
    mobileSelectedDateIso,
    mobileDayEvents,
    loadingDonorCenterCalendar,
    tenantTodayIso,
    procedureFilter,
    mobileBookableRow,
  ])

  const year = viewMonth.getFullYear()
  const month = viewMonth.getMonth()
  const firstWeekday = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const handlePrevMonth = () => {
    const floor = getTenantMonthFloorDate()
    setViewMonth(d => {
      const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1)
      return prev.getTime() < floor.getTime() ? d : prev
    })
  }

  const handleNextMonth = () => {
    setViewMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))
  }

  const tenantEarliestMonthStart = getTenantMonthFloorDate()
  const canGoToPreviousMonth =
    viewMonth.getTime() > tenantEarliestMonthStart.getTime()

  const toggleDayExpand = useCallback(dayKey => {
    setExpandedDays(prev => ({ ...prev, [dayKey]: !prev[dayKey] }))
  }, [])

  const handleSelectSession = useCallback(
    (raw, dateStr) => {
      const sessionDay = dateStr || raw.date
      if (sessionDay && sessionDay < tenantTodayIso) return

      const { selectable } = getDonorEligibilityForScheduleRow({
        donorId: donor?.id,
        procedureEligibilities: donor?.procedure_eligibilities,
        selectedProcedureTypeId: procedureFilter,
        appointmentDayIso: sessionDay,
        procedureTypesOnRow: raw?.procedure_types,
      })
      if (donor?.id && !selectable) return

      const withDate = { ...raw, date: dateStr || raw.date }
      const built = buildResultItem(withDate, {})
      const payload = {
        ...built,
        date: withDate.date,
        procedure_type_id: procedureFilter || undefined,
        address: {
          latitude: built.latitude,
          longitude: built.longitude,
          formatted_address: '',
        },
      }

      navigate(
        `${ROUTES.SCHEDULE_DONATION}?${getScheduledDonationsParams(
          payload
        ).toString()}`
      )
    },
    [donor, navigate, procedureFilter, tenantTodayIso]
  )

  const handleMobileContinue = useCallback(() => {
    if (!mobileSelectedDateIso || !mobileBookableRow || mobileDaySlots <= 0) {
      return
    }
    handleSelectSession(mobileBookableRow, mobileSelectedDateIso)
  }, [
    mobileBookableRow,
    mobileDaySlots,
    mobileSelectedDateIso,
    handleSelectSession,
  ])

  if (!parsed.donor_center_id) {
    return <Navigate to={ROUTES.DONATE_DONOR_CENTERS} replace />
  }

  const monthLabel = format(viewMonth, 'MMMM yyyy')

  const sessionCountLabel = `${availabilityInsight.sessionRows} ${availabilityInsight.sessionRows === 1 ? 'day' : 'days'}`
  const slotSumLabel = `${availabilityInsight.totalSlots} ${availabilityInsight.totalSlots === 1 ? 'slot' : 'slots'}`
  const sessionsAndSlotsLabel = `${sessionCountLabel} · ${slotSumLabel}`

  const availabilityHeadline = loadingDonorCenterCalendar
    ? `Checking availability for ${monthLabel}…`
    : availabilityInsight.sessionRows === 0
      ? `No openings in ${monthLabel} for this filter.`
      : `${sessionsAndSlotsLabel}`

  const cells = []
  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push({ type: 'pad', key: `pad-${i}` })
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const dateStr = toUTCDateString(new Date(year, month, day))
    cells.push({ type: 'day', day, dateStr, key: dateStr })
  }

  const mobileSelectedDateLabel = mobileSelectedDateIso
    ? format(
        parse(mobileSelectedDateIso, 'yyyy-MM-dd', new Date()),
        'EEEE, MMMM d, yyyy'
      )
    : ''

  return (
    <Stack spacing={2} sx={{ pb: 4 }}>
      <DonorPortalScreenMessage
        code={DONOR_PORTAL_SCREEN_CONFIG_CODE.SEARCH_BY_DONOR_CENTERS_RESULTS}
      />
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          background: t =>
            `linear-gradient(145deg, ${alpha(t.palette.primary.main, 0.07)} 0%, ${t.palette.background.paper} 42%, ${t.palette.background.paper} 100%)`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              mb: 0.5,
            }}
          >
            <Typography
              variant='overline'
              color='text.secondary'
              sx={{ letterSpacing: 0.8, fontWeight: 700 }}
            >
              Donor center
            </Typography>
            <Link
              component={RouterLink}
              to={ROUTES.DONATE_DONOR_CENTERS}
              variant='body2'
              underline='hover'
              sx={{
                fontWeight: 600,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                mt: 0.25,
              }}
            >
              Change donor center
            </Link>
          </Box>
          <Typography
            variant='h5'
            component='h1'
            fontWeight={800}
            sx={{
              mb: 1,
              letterSpacing: -0.02,
              lineHeight: 1.2,
            }}
          >
            {donorCenterName}
          </Typography>
          {locationPreview?.address ? (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mb: 0.5, lineHeight: 1.5 }}
            >
              {locationPreview.address}
            </Typography>
          ) : null}
          {locationPreview?.phone ? (
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              {locationPreview.phone}
            </Typography>
          ) : null}
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          boxShadow: t =>
            `0 1px 2px ${alpha(t.palette.common.black, 0.04)}, 0 12px 40px ${alpha(t.palette.common.black, 0.06)}`,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.75,
            bgcolor: theme =>
              alpha(
                theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.12 : 0.04
              ),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: 2,
              columnGap: 2,
            }}
          >
            <Stack
              direction='row'
              spacing={0.25}
              alignItems='center'
              sx={{
                flexShrink: 0,
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              <IconButton
                id='dcc-prev-month'
                aria-label='Previous month'
                onClick={handlePrevMonth}
                size='small'
                disabled={!canGoToPreviousMonth}
                sx={{
                  width: 28,
                  height: 28,
                  p: 0.375,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                  '& svg': { fontSize: '1.125rem' },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                variant='subtitle1'
                fontWeight={800}
                sx={{ px: 1.25, minWidth: 140, textAlign: 'center' }}
              >
                {monthLabel}
              </Typography>
              <IconButton
                id='dcc-next-month'
                aria-label='Next month'
                onClick={handleNextMonth}
                size='small'
                sx={{
                  width: 28,
                  height: 28,
                  p: 0.375,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                  '& svg': { fontSize: '1.125rem' },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                textAlign: 'center',
              }}
            >
              <Typography
                variant='body2'
                fontWeight={600}
                color={
                  availabilityInsight.sessionRows === 0 &&
                  !loadingDonorCenterCalendar
                    ? 'text.secondary'
                    : 'text.primary'
                }
                sx={{ lineHeight: 1.35 }}
              >
                {availabilityHeadline}
              </Typography>
            </Box>

            <FormControl
              size='small'
              disabled={loadingProcedureTypes || loadingDonorCenterCalendar}
              sx={{
                flexShrink: 0,
                width: { xs: '100%', md: 'auto' },
                minWidth: { xs: '100%', md: 200 },
                maxWidth: { xs: '100%', md: 320 },
                bgcolor: 'background.paper',
                borderRadius: 1,
                alignSelf: { xs: 'stretch', md: 'center' },
                ml: { xs: 0, md: 'auto' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme =>
                    alpha(
                      theme.palette.divider,
                      theme.palette.mode === 'dark' ? 1 : 0.9
                    ),
                },
              }}
            >
              <InputLabel id='dcc-calendar-procedure-label'>
                Procedure
              </InputLabel>
              <Select
                labelId='dcc-calendar-procedure-label'
                label='Procedure'
                value={procedureFilter}
                onChange={e => setProcedureFilter(e.target.value)}
              >
                <MenuItem value=''>
                  <em>All procedures</em>
                </MenuItem>
                {(procedureTypes || []).map(pt => (
                  <MenuItem key={pt.id} value={String(pt.id)}>
                    {pt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loadingDonorCenterCalendar && <LinearProgress color='primary' />}

        <Box
          sx={{
            p: { xs: 1.25, sm: 2 },
            bgcolor: theme =>
              alpha(
                theme.palette.grey[500],
                theme.palette.mode === 'dark' ? 0.08 : 0.04
              ),
            overflow: { xs: 'visible', md: 'auto' },
          }}
        >
          {isMobile ? (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 0.75,
                  width: '100%',
                  maxWidth: 380,
                  mx: 'auto',
                  pb: 1.25,
                }}
              >
                {WEEKDAYS.map(w => (
                  <Typography
                    key={`m-${w}`}
                    variant='caption'
                    fontWeight={800}
                    color='text.secondary'
                    sx={{
                      textAlign: 'center',
                      py: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.62rem',
                    }}
                  >
                    {w}
                  </Typography>
                ))}
                {cells.map(cell => {
                  if (cell.type === 'pad') {
                    return <Box key={cell.key} sx={{ minHeight: 10 }} />
                  }
                  const events = sessionsByDate[cell.dateStr] || []
                  const hasOpenings = events.length > 0
                  const anchorHighlight = cell.dateStr === parsed.date
                  const isTodayCell = cell.dateStr === tenantTodayIso
                  const meta = calendarDayMetaByDate[cell.dateStr]
                  const isPastDay =
                    meta?.isPastDay ?? cell.dateStr < tenantTodayIso
                  const isClosed = Boolean(meta?.isClosed)
                  const isFull = Boolean(meta?.isFull)
                  const isNotEligible = Boolean(meta?.isNotEligible)
                  const selectable = Boolean(meta?.selectable)
                  const selected = mobileSelectedDateIso === cell.dateStr

                  let marker = null
                  if (isPastDay) {
                    marker = null
                  } else if (isClosed || isFull) {
                    marker = (
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.grey[600], 0.55),
                          flexShrink: 0,
                        }}
                      />
                    )
                  } else if (isNotEligible) {
                    marker = (
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          bgcolor: theme.palette.warning.main,
                          flexShrink: 0,
                        }}
                      />
                    )
                  } else if (hasOpenings) {
                    marker = (
                      <Box
                        sx={{
                          width: 13,
                          height: 3,
                          borderRadius: 999,
                          bgcolor: theme.palette.success.main,
                          flexShrink: 0,
                        }}
                      />
                    )
                  }

                  const cursorStyle =
                    isPastDay || !selectable ? 'not-allowed' : 'pointer'

                  let cellBg = 'transparent'
                  if (selected && selectable) {
                    cellBg = alpha(theme.palette.primary.main, 0.2)
                  } else if (!isPastDay && (isClosed || isFull)) {
                    cellBg = alpha(
                      theme.palette.grey[600],
                      theme.palette.mode === 'dark' ? 0.22 : 0.14
                    )
                  } else if (!isPastDay && isNotEligible) {
                    cellBg = alpha(theme.palette.warning.main, 0.16)
                  }

                  let cellShadow = 'none'
                  if (selected && selectable) {
                    cellShadow = `inset 0 0 0 2px ${theme.palette.primary.main}`
                  } else if (isTodayCell && selectable && !selected) {
                    cellShadow = `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.55)}`
                  } else if (!isPastDay && (isClosed || isFull)) {
                    cellShadow = `inset 0 0 0 1px ${alpha(theme.palette.grey[600], 0.35)}`
                  } else if (!isPastDay && isNotEligible) {
                    cellShadow = `inset 0 0 0 1px ${alpha(theme.palette.warning.main, 0.45)}`
                  }

                  return (
                    <Box
                      key={cell.key}
                      component='button'
                      type='button'
                      disabled={!selectable}
                      aria-disabled={!selectable}
                      aria-label={
                        isPastDay
                          ? `${cell.day}, past date`
                          : isClosed
                            ? `${cell.day}, no operations`
                            : isFull
                              ? `${cell.day}, not available`
                              : isNotEligible
                                ? `${cell.day}, not eligible`
                                : `${cell.day}, select date`
                      }
                      onClick={() => {
                        if (selectable) setMobileSelectedDateIso(cell.dateStr)
                      }}
                      ref={el => {
                        if (anchorHighlight && el)
                          dayRefs.current[cell.dateStr] = el
                      }}
                      sx={{
                        aspectRatio: '1',
                        maxWidth: 44,
                        mx: 'auto',
                        width: '100%',
                        border: 'none',
                        borderRadius: '50%',
                        cursor: cursorStyle,
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.25,
                        bgcolor: cellBg,
                        boxShadow: cellShadow,
                        opacity: isPastDay ? 0.38 : 1,
                        '&:disabled': {
                          opacity: isPastDay ? 0.38 : 0.85,
                          cursor: cursorStyle,
                          WebkitTextFillColor: 'unset',
                          color: 'inherit',
                        },
                      }}
                    >
                      <Typography
                        variant='body2'
                        fontWeight={800}
                        sx={{
                          fontSize: '0.9rem',
                          lineHeight: 1,
                          color: isPastDay
                            ? 'text.disabled'
                            : isClosed || isFull
                              ? 'text.secondary'
                              : isNotEligible
                                ? 'warning.dark'
                                : isTodayCell && selectable
                                  ? 'primary.main'
                                  : 'text.primary',
                        }}
                      >
                        {cell.day}
                      </Typography>
                      <Box
                        sx={{
                          height: 6,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        {marker}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
              <Stack
                direction='row'
                flexWrap='wrap'
                justifyContent='center'
                alignItems='center'
                columnGap={2}
                rowGap={0.75}
                sx={{ pb: 0.75, px: 0.5, maxWidth: 380, mx: 'auto' }}
              >
                <Stack direction='row' alignItems='center' spacing={0.75}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.grey[600], 0.55),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  >
                    Not available
                  </Typography>
                </Stack>
                <Stack direction='row' alignItems='center' spacing={0.75}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: theme.palette.warning.main,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  >
                    Not eligible
                  </Typography>
                </Stack>
                <Stack direction='row' alignItems='center' spacing={0.75}>
                  <Box
                    sx={{
                      width: 14,
                      height: 4,
                      borderRadius: 999,
                      bgcolor: theme.palette.success.main,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  >
                    Has openings
                  </Typography>
                </Stack>
              </Stack>
              {mobileSelectedDateIso ? (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 0.5,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                      columnGap: 1.25,
                      rowGap: 0.75,
                    }}
                  >
                    <Typography
                      variant='subtitle1'
                      fontWeight={800}
                      sx={{ flex: '1 1 auto', minWidth: 0, lineHeight: 1.3 }}
                    >
                      {mobileSelectedDateLabel}
                    </Typography>
                    {mobileEligibilityChipConfig ? (
                      <Chip
                        size='small'
                        label={mobileEligibilityChipConfig.label}
                        color={
                          mobileEligibilityChipConfig.eligible
                            ? 'success'
                            : 'default'
                        }
                        variant={
                          mobileEligibilityChipConfig.eligible
                            ? 'filled'
                            : 'outlined'
                        }
                        sx={{
                          flexShrink: 0,
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: 22,
                          '& .MuiChip-label': { px: 0.75, py: 0 },
                        }}
                      />
                    ) : null}
                  </Box>
                  {mobileTimeSpanLabel ? (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 0.75 }}
                    >
                      {mobileTimeSpanLabel}
                    </Typography>
                  ) : null}
                  <Typography
                    variant='body2'
                    sx={{ mt: 1.25 }}
                    fontWeight={600}
                  >
                    {mobileDaySlots === 0
                      ? 'No openings left on this date.'
                      : `${mobileDaySlots} opening${mobileDaySlots === 1 ? '' : 's'} available`}
                  </Typography>
                  {mobileBookableRow && mobileDaySlots > 0 ? (
                    <CommonButton
                      id='dcc-mobile-continue'
                      sx={{ mt: 2 }}
                      fullWidth
                      variant='primary'
                      color='primary'
                      size='medium'
                      label='Continue'
                      onClick={handleMobileContinue}
                    />
                  ) : null}
                </Paper>
              ) : null}
            </>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(92px, 1fr))',
                gap: { xs: 0.65, sm: 1 },
                minWidth: { xs: 620, sm: 'auto' },
              }}
            >
              {WEEKDAYS.map(w => (
                <Typography
                  key={w}
                  variant='caption'
                  fontWeight={800}
                  color='text.secondary'
                  sx={{
                    textAlign: 'center',
                    py: 1,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    fontSize: '0.68rem',
                  }}
                >
                  {w}
                </Typography>
              ))}

              {cells.map(cell => {
                if (cell.type === 'pad') {
                  return (
                    <Box
                      key={cell.key}
                      sx={{
                        minHeight: 124,
                        borderRadius: 2,
                        bgcolor: 'transparent',
                      }}
                    />
                  )
                }

                const events = sessionsByDate[cell.dateStr] || []
                const isExpanded = expandedDays[cell.dateStr]
                const visibleEvents =
                  isExpanded || events.length <= MAX_COMPACT_EVENTS
                    ? events
                    : events.slice(0, MAX_COMPACT_EVENTS)
                const showToggle = events.length > MAX_COMPACT_EVENTS
                const anchorHighlight = cell.dateStr === parsed.date
                const isTodayCell = cell.dateStr === tenantTodayIso
                const isPastDay = cell.dateStr < tenantTodayIso

                const appointmentDayEligibility =
                  !isPastDay && donor?.id && events.length > 0
                    ? getDonorEligibilityForCalendarDay({
                        donorId: donor.id,
                        procedureEligibilities: donor.procedure_eligibilities,
                        selectedProcedureTypeId: procedureFilter,
                        appointmentDayIso: cell.dateStr,
                        sessionRows: events,
                      })
                    : null

                let dayEligibilityChip = null
                if (
                  !loadingDonorCenterCalendar &&
                  procedureFilter &&
                  appointmentDayEligibility?.applies &&
                  appointmentDayEligibility.chip
                ) {
                  const { status, label } = appointmentDayEligibility.chip
                  const color = status === 'eligible' ? 'success' : 'default'
                  dayEligibilityChip = (
                    <Chip
                      size='small'
                      label={label}
                      color={color}
                      variant={status === 'eligible' ? 'filled' : 'outlined'}
                      sx={{
                        alignSelf: 'stretch',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 22,
                        '& .MuiChip-label': { px: 0.75, py: 0 },
                      }}
                    />
                  )
                }

                return (
                  <Paper
                    key={cell.key}
                    elevation={0}
                    ref={el => {
                      if (anchorHighlight && el)
                        dayRefs.current[cell.dateStr] = el
                    }}
                    sx={{
                      minHeight: 124,
                      p: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.65,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isPastDay
                        ? alpha(
                            theme.palette.divider,
                            theme.palette.mode === 'dark' ? 0.22 : 0.28
                          )
                        : isTodayCell
                          ? 'primary.main'
                          : anchorHighlight
                            ? alpha(theme.palette.primary.main, 0.38)
                            : alpha(
                                theme.palette.divider,
                                theme.palette.mode === 'dark' ? 0.48 : 0.42
                              ),
                      bgcolor: theme =>
                        isPastDay
                          ? alpha(
                              theme.palette.grey[500],
                              theme.palette.mode === 'dark' ? 0.14 : 0.06
                            )
                          : anchorHighlight
                            ? alpha(theme.palette.primary.main, 0.09)
                            : theme.palette.background.paper,
                      opacity: isPastDay ? 0.62 : 1,
                      boxShadow: isPastDay
                        ? 'none'
                        : isTodayCell
                          ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`
                          : `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
                      maxHeight: 320,
                      overflow: 'hidden',
                      pointerEvents: isPastDay ? 'none' : undefined,
                      transition: theme =>
                        theme.transitions.create(
                          [
                            'border-color',
                            'background-color',
                            'box-shadow',
                            'opacity',
                          ],
                          { duration: theme.transitions.duration.shorter }
                        ),
                      ...(isPastDay
                        ? {}
                        : {
                            '&:hover': {
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.32
                              ),
                              boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.06)}`,
                            },
                          }),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant='body2'
                        component='span'
                        fontWeight={800}
                        sx={{
                          fontSize: '0.95rem',
                          color: isPastDay
                            ? 'text.disabled'
                            : isTodayCell
                              ? 'primary.main'
                              : 'text.primary',
                          lineHeight: 1,
                        }}
                      >
                        {cell.day}
                      </Typography>
                      {isTodayCell ? (
                        <Typography
                          variant='caption'
                          sx={{
                            color: 'primary.main',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          Today
                        </Typography>
                      ) : null}
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.65,
                        minHeight: 0,
                        pt: 0,
                        pb: 0.75,
                        pr: 0.25,
                        '&::-webkit-scrollbar': { width: 5 },
                        '&::-webkit-scrollbar-thumb': {
                          borderRadius: 4,
                          bgcolor: 'action.selected',
                        },
                      }}
                    >
                      {visibleEvents.length === 0
                        ? null
                        : visibleEvents.map((row, idx) => {
                            const built = buildResultItem(row, {})
                            const slots = Number(row?.available_slots_count)
                            const noSlots = Number.isNaN(slots) || slots <= 0
                            const elig = getDonorEligibilityForScheduleRow({
                              donorId: donor?.id,
                              procedureEligibilities:
                                donor?.procedure_eligibilities,
                              selectedProcedureTypeId: procedureFilter,
                              appointmentDayIso: cell.dateStr,
                              procedureTypesOnRow: row?.procedure_types,
                            })
                            const chipDisabled =
                              noSlots || (elig.applies && !elig.selectable)
                            return (
                              <CalendarSessionChip
                                key={`${cell.dateStr}-${row.operation_id}-${idx}`}
                                item={built}
                                disabled={chipDisabled}
                                eligibilityChip={
                                  !procedureFilter && elig.applies
                                    ? elig.chip
                                    : null
                                }
                                onClick={() =>
                                  handleSelectSession(row, cell.dateStr)
                                }
                              />
                            )
                          })}
                    </Box>

                    {dayEligibilityChip}

                    {showToggle ? (
                      <Link
                        component='button'
                        type='button'
                        variant='caption'
                        fontWeight={600}
                        onClick={e => {
                          e.preventDefault()
                          toggleDayExpand(cell.dateStr)
                        }}
                        sx={{
                          cursor: 'pointer',
                          alignSelf: 'flex-start',
                          mt: 0.25,
                        }}
                      >
                        {isExpanded
                          ? 'Show less'
                          : `${events.length - MAX_COMPACT_EVENTS} more…`}
                      </Link>
                    ) : null}
                  </Paper>
                )
              })}
            </Box>
          )}
        </Box>
      </Paper>
    </Stack>
  )
}

export default DonorCenterCalendar
